/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { getId } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { createSimpleEntityStore } from 'src/luis/core/stores/simpleEntityStore';
import { EntityTree } from 'src/luis/modules/entities/components/EntityTree';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';

const messages = defineMessages({
    mlEntityDescription: {
        id: 'entities.createEntityModal.mlEntityDescription',
        defaultMessage:
            'An ML entity can be composed of smaller subentities, each of which can have its own properties. You can add subentities when you create your ML entity, and you can add subentities to existing ML entities.'
    },
    mlEntityNoteLabel: {
        id: 'entities.createEntityModal.mlEntityNoteLabel ',
        defaultMessage: 'Note:'
    },
    mlEntityNoteText: {
        id: 'entities.createEntityModal.mlEntityNoteText ',
        defaultMessage: 'Once you have created your entity you will not be able to rearrange subentities.'
    }
});

const { styleComponent } = getStylistV2('CreateMLEntityScreen');

const MLEntitiesDescriptionContainer = styleComponent<IStackProps>(Stack)('MLEntitiesDescriptionContainer', {
    marginBottom: '14px'
});

const MLEntitiesNoteContainer = styleComponent<IStackProps>(Stack)('MLEntitiesNoteContainer', {
    marginBottom: '36px'
});

const MLEntityTreeContainer = styleComponent<IStackProps>(Stack)('MLEntityTreeContainer', theme => ({
    backgroundColor: theme.mlTreeContainerBackgroundColor,
    minHeight: '324px',
    paddingTop: '5px',
    paddingBottom: '5px'
}));

const MLEntitiesNoteLabel = styleComponent<LuisTextProps>(LuisText)('MLEntitiesNoteLabel', {
    marginRight: '6px'
});

/**
 * Props for CreateMachineLearnedEntityScreenV2
 */
export type CreateMLEntityScreenProps = {
    /**
     * The name of ML entity being created (aka the root). This was defined in the previous screen.
     */
    newEntityName: string;
    /**
     * Callback for updating the parent component with the latest information about the entity being created.
     */
    onChange: (entityStore: SimpleEntityStore) => void;
};

/**
 * A wrapper around the entity tree, maintaining in its state a local Entity, handling adding and deleting children as well as renaming.
 */
export const CreateMLEntityScreen = observer((props: CreateMLEntityScreenProps) => {
    const { newEntityName, onChange } = props;

    const { localize } = useLocalization();
    const localEntityStore = useLocalStore(() => createSimpleEntityStore(getId(), { name: newEntityName }));

    React.useEffect(() => {
        onChange(localEntityStore);
    }, [localEntityStore.updateCount]);

    const addEntity = (rootEntityId: LuisId, parentEntityId: LuisId, entityName: string) => {
        const parentEntity = parentEntityId === rootEntityId ? localEntityStore : localEntityStore.getChildById(parentEntityId);

        parentEntity.addChild(getId(), { name: entityName });
        localEntityStore.incrementUpdateCount();
    };

    const removeEntity = (_: LuisId, entityStore: EntityStore) => {
        localEntityStore.removeChildById(entityStore.id);
        localEntityStore.incrementUpdateCount();
    };

    const renameEntity = (_: EntityStore, newName: string) => {
        localEntityStore.rename(newName);
        localEntityStore.incrementUpdateCount();
    };

    const renameChildEntity = (_: LuisId, entityId: LuisId, newName: string) => {
        const entityToRename = localEntityStore.getChildById(entityId);
        entityToRename.rename(newName);
        localEntityStore.incrementUpdateCount();
    };

    return (
        <>
            <MLEntitiesDescriptionContainer>
                <LuisText>{localize(messages.mlEntityDescription)}</LuisText>
            </MLEntitiesDescriptionContainer>
            <MLEntitiesNoteContainer horizontal>
                <MLEntitiesNoteLabel fontWeight={FontWeights.semibold as Property.FontWeight}>
                    {localize(messages.mlEntityNoteLabel)}
                </MLEntitiesNoteLabel>
                <LuisText>{localize(messages.mlEntityNoteText)}</LuisText>
            </MLEntitiesNoteContainer>
            <MLEntityTreeContainer>
                <EntityTree
                    entityStores={[localEntityStore]}
                    autoFocusFirstEntityItem
                    onCreateChildEntity={addEntity}
                    onDeleteEntity={removeEntity}
                    onRenameEntity={renameEntity}
                    onRenameChildEntity={renameChildEntity}
                />
            </MLEntityTreeContainer>
        </>
    );
});
